/**
 * 역할 등급(rank)과 위임 규칙 — 서버 `apps/chat-server/src/modules/admin/role-rank.ts` 와 같은 규칙.
 *
 * 근태 › 사용자 관리(2026-08-25)를 위해 관리 콘솔의 `admin/frontend/src/lib/roleRank.ts` 를
 * 그대로 옮겨 왔다. 세 벌(서버·콘솔·사용자 사이트)이 같은 규칙을 말해야 하므로 내용을 바꾸지 않는다.
 *
 * 화면은 **판정자가 아니라 안내자**다. 최종 판정은 서버가 하고(ROLE_ESCALATION_DENIED /
 * CANNOT_MANAGE_HIGHER_ROLE / CANNOT_CHANGE_HIGHER_ROLE_PASSWORD), 여기서는 고를 수 없는 값을
 * 애초에 보여 주지 않아 "눌렀는데 403" 을 줄인다.
 *
 * 규칙: 숫자가 작을수록 높은 권한이고, 자기 등급보다 높은 권한은 주지도 건드리지도 못한다.
 */

export type ManagedRole = 'SYSTEM_ADMIN' | 'ADMIN' | 'MANAGER' | 'STAFF';

export const ROLE_RANK: Readonly<Record<string, number>> = {
  SYSTEM_ADMIN: 0,
  ADMIN: 1,
  MANAGER: 2,
  STAFF: 3,
};

/** 등급표 밖(AI_BOT 등) — 비교식이 항상 거짓이 되도록. */
export const UNRANKED = 99;

/** 높은 권한 → 낮은 권한 순. 셀렉트 항목의 표시 순서이기도 하다. */
export const ROLE_ORDER: ManagedRole[] = ['SYSTEM_ADMIN', 'ADMIN', 'MANAGER', 'STAFF'];

export const ROLE_OPTION_LABEL: Record<ManagedRole, string> = {
  SYSTEM_ADMIN: '시스템관리자 (SYSTEM_ADMIN)',
  ADMIN: '관리자 (ADMIN)',
  MANAGER: '매니저 (MANAGER)',
  STAFF: '직원 (STAFF)',
};

export function rankOf(role: string | null | undefined): number {
  return (role && ROLE_RANK[role] !== undefined) ? ROLE_RANK[role]! : UNRANKED;
}

export function isAdminRole(role: string | null | undefined): boolean {
  return role === 'ADMIN' || role === 'SYSTEM_ADMIN';
}

/** actor 가 target 역할의 계정을 다룰 수 있는가 — 같은 등급까지 허용, 위는 불가. */
export function canManageRole(actorRole: string | null | undefined, targetRole: string): boolean {
  if (targetRole === 'AI_BOT') return false;
  const a = rankOf(actorRole);
  if (a === UNRANKED) return false;
  return a <= rankOf(targetRole);
}

/** actor 가 만들거나 부여할 수 있는 역할 — 높은 권한 → 낮은 권한 순. */
export function assignableRolesFor(actorRole: string | null | undefined): ManagedRole[] {
  const a = rankOf(actorRole);
  if (a === UNRANKED) return [];
  return ROLE_ORDER.filter((r) => a <= ROLE_RANK[r]!);
}
